// @ts-strict-ignore
import {Location} from 'react-router';

import {FormattedProperty, getRawPropertyName} from 'common/util/format-property';
import {
  ALL_MATH_TYPES,
  ChartType,
  DATASET_MIXPANEL,
  PropertyType,
  ValueRepresentationType,
} from 'common/report/constants';
import {MetricType} from 'common/query-builder/query-block/constants';
import {
  AnalysisType,
  ChartPlotStyle,
  FilterClause as FilterClauseType,
  GroupClause as GroupClauseType,
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsResourceType,
  ShowClause as ShowClauseType,
  TimeClause as TimeClauseType,
} from 'common/report/insights/util/types';
import {MPProperties} from 'common/constants';
import {StringOperator} from 'common/widgets/property-filter-menu/models/types';
import {DATE_RANGE_TYPE} from 'common/report/insights/models/time-clause';
import {TIME_UNITS} from 'common/report/insights/models/constants';
import {queryModule} from 'common/report/report-manager/query';
import {formatDate} from 'common/util/date';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {_getDefaultInsightsSorting} from 'common/report/insights/util';

export function createInsightsBookmarkParams({
  scdProperty,
  scdEvent,
  dataGroupId,
  distinctId,
}: {
  scdProperty: FormattedProperty;
  scdEvent: string;
  dataGroupId: string;
  distinctId: string;
}): InsightsBookmarkParams {
  const show: ShowClauseType[] = [
    {
      behavior: {
        type: MetricType.Event,
        resourceType: InsightsResourceType.Events,
        dataGroupId,
        dataset: DATASET_MIXPANEL,
        filters: [],
        name: scdEvent,
        profileType: null,
        search: ``,
      },
      measurement: {
        math: ALL_MATH_TYPES.UNIQUE,
        cumulative: false,
        perUserAggregation: null,
        property: null,
        rolling: null,
      },
    },
  ];

  const filter: FilterClauseType[] = [
    {
      dataset: DATASET_MIXPANEL,
      value: MPProperties.DistinctId,
      resourceType: InsightsResourceType.Events,
      profileType: null,
      search: ``,
      dataGroupId,
      filterType: PropertyType.String,
      defaultType: PropertyType.String,
      filterOperator: StringOperator.Equals,
      filterValue: [distinctId],
      propertyObjectKey: null,
    },
  ];

  const group: GroupClauseType[] = [
    {
      dataset: DATASET_MIXPANEL,
      value: MPProperties.$Time,
      resourceType: InsightsResourceType.Events,
      profileType: null,
      search: ``,
      dataGroupId,
      propertyType: PropertyType.Datetime,
      typeCast: null,
      unit: null,
    },
    {
      dataset: DATASET_MIXPANEL,
      value: getRawPropertyName(scdProperty),
      resourceType: InsightsResourceType.People,
      profileType: null,
      search: ``,
      dataGroupId,
      scdEvent,
      propertyType: PropertyType.String,
      typeCast: null,
      unit: null,
    },
  ];

  // TODO(CC): improve the performance by setting the from date to the date when
  // property change first happened.
  const time: TimeClauseType[] = [
    {
      dateRangeType: DATE_RANGE_TYPE.SINCE,
      unit: TIME_UNITS.DAY,
      value: [`2000-01-01T00:00:00`],
    },
  ];

  return {
    sections: {
      cohorts: [],
      formula: [],
      show,
      filter,
      group,
      time,
      globalDataGroupId: dataGroupId,
    },
    sorting: _getDefaultInsightsSorting({numSegmentColumns: 0, canUseViewN: true}),
    columnWidths: {
      bar: {},
    },
    displayOptions: {
      chartType: ChartType.Bar,
      plotStyle: ChartPlotStyle.Standard,
      analysis: AnalysisType.Linear,
      value: ValueRepresentationType.Absolute,
    },
  };
}

export async function getPropertyChangeLog({
  bookmarkParams,
  projectId,
  worksapceId,
  location,
}: {
  bookmarkParams: InsightsBookmarkParams;
  projectId: string;
  worksapceId: string;
  location: Location;
}): Promise<Array<[string, string]>> {
  const response = await queryModule.executeReportQuery({
    endpoint: BookmarkType.Insights,
    queryParams: {
      workspace_id: worksapceId,
      project_id: projectId,
    },
    body: {
      bookmark: bookmarkParams,
      use_query_cache: true,
      use_query_sampling: false,
      report_query_origin: BookmarkType.Insights,
      tracking_props: {},
    },
    throwOnInvalidStatus: false,
    location,
  });
  const results: InsightsQueryResults = await response.json();

  // format results where key is timestamp and value is segment key
  // i.e. {"Jan 1, 2024": "Free", "Feb 12, 2024": "Premium"}
  const formattedResults: Array<[string, string]> = [];
  const seriesTree = Object.values(results.series)[0];
  if (seriesTree) {
    delete seriesTree[`$overall`];
    let prevSegmentKey: string;
    // sort by date
    const sortedSeriesTree = Object.entries(seriesTree).sort(
      (a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime(),
    );
    for (const [timestamp, segmentKeys] of sortedSeriesTree) {
      delete segmentKeys[`$overall`];
      const segmentKey = Object.keys(segmentKeys)[0];
      if (segmentKey !== prevSegmentKey) {
        const formattedDate = formatDate(new Date(timestamp));
        formattedResults.push([formattedDate, segmentKey]);
      }
      prevSegmentKey = segmentKey;
    }
  }

  return formattedResults;
}
